import { create } from "zustand";

/**
 * Cross-route draft for the next new-session agent. Set when the user
 * picks an agent from the sidebar (on /agents or elsewhere); read by
 * ChatPage to stamp the ref into the upcoming `POST /api/sessions`.
 * Cleared on session select so it never leaks into an active chat.
 *
 * `pendingConnectorIds` is the hero-time override of the agent's default
 * connector set. `null` = "no override yet, use the agent's defaults". A
 * list (possibly empty) = "user has explicitly toggled, send this to the
 * server on session create".
 */
interface AgentDraftStore {
  pendingRef: string | null;
  pendingConnectorIds: string[] | null;
  /** Text the user typed on a different page (e.g. the space dashboard
   *  hero input) that should auto-send when ChatPage mounts in hero mode.
   *  Consumed exactly once — the consumer clears it via setPendingText(null)
   *  after firing. */
  pendingText: string | null;
  /** Slash-command label for a draft sent from the dashboard composer, so
   *  the handed-over message renders as a compact chip just like in a
   *  personal-space chat. Consumed once alongside pendingText. */
  pendingCommand: { label: string } | null;
  /** Folder picked on the dashboard composer (before a session exists).
   *  ChatPage attaches it via `docs.finishConnect(handle, "merge")` once the
   *  first message creates a session, then clears it. Not persisted — the
   *  FileSystemDirectoryHandle's permission grant is per-tab anyway, so a
   *  refresh would lose it; user re-picks if needed. */
  pendingFolderHandle: FileSystemDirectoryHandle | null;
  /** Network-folder (WebDAV) selection picked in hero mode. Imported into the
   *  workspace by ChatPage's handleSend once the first message creates a
   *  session, then cleared. Mirrors `pendingFolderHandle` for the local case. */
  pendingWebdavItems: { path: string; isDir: boolean }[] | null;
  /** Files picked via the composer "+" in hero mode (before a session
   *  exists). Uploaded into the new session's `attachments/` dir by
   *  ChatPage's handleSend after the first message creates it, then cleared.
   *  One-shot — not folder-synced. Mirrors the pendingFolderHandle pattern. */
  pendingFiles: File[] | null;
  /** True while a draft is being consumed and the new session is being
   *  created. Bridges the gap between clearing pendingText and sendMessage
   *  flipping activeId, so transitional UI (hero block, sidebar pill) stays
   *  in chat mode instead of flashing back to the welcome screen. */
  starting: boolean;
  /** Hero folder-sync activity for the session created from the hero
   *  composer. Single source for: the optimistic user bubble + the sync
   *  step in MessageList, the uploading card in FilesPanel, and the
   *  Send-disable in InputBar. While `uploading` the step shows a pulsing
   *  dot + "Синхронизирую файлы …" and /runs is gated; once `done` the
   *  step settles into the turn rail as a completed step. Ephemeral —
   *  lives only for the open session view (not persisted), so a reload
   *  shows just the agent's own server-backed steps. Keyed by sessionId
   *  so it renders only on the session it belongs to. */
  heroSync: HeroSync | null;
  setPendingRef: (ref: string | null) => void;
  setPendingConnectorIds: (ids: string[] | null) => void;
  setPendingText: (text: string | null) => void;
  setPendingCommand: (command: { label: string } | null) => void;
  setPendingFolderHandle: (handle: FileSystemDirectoryHandle | null) => void;
  setPendingWebdavItems: (items: { path: string; isDir: boolean }[] | null) => void;
  setPendingFiles: (files: File[] | null) => void;
  setStarting: (v: boolean) => void;
  setHeroSync: (s: HeroSync | null) => void;
}

export interface HeroSync {
  sessionId: string;
  text: string;
  folderName: string | null;
  current: number;
  total: number;
  status: "uploading" | "done";
  /** "files" = one-shot "+" attachments (label "прикрепляю/прикреплены");
   *  "folder" / absent = a synced folder source ("синхронизирую/-а"). */
  kind?: "folder" | "files";
}

export const useAgentDraftStore = create<AgentDraftStore>((set) => ({
  pendingRef: null,
  pendingConnectorIds: null,
  pendingText: null,
  pendingCommand: null,
  pendingFolderHandle: null,
  pendingWebdavItems: null,
  pendingFiles: null,
  starting: false,
  heroSync: null,
  // Switching the agent resets connectors — the new agent's defaults take
  // over and the user starts fresh. Keeping the previous override would let
  // a stray toggle from agent A leak into agent B.
  setPendingRef: (ref) => set({ pendingRef: ref, pendingConnectorIds: null }),
  setPendingConnectorIds: (ids) => set({ pendingConnectorIds: ids }),
  setPendingText: (text) => set({ pendingText: text }),
  setPendingCommand: (command) => set({ pendingCommand: command }),
  setPendingFolderHandle: (handle) => set({ pendingFolderHandle: handle }),
  setPendingWebdavItems: (items) => set({ pendingWebdavItems: items }),
  setPendingFiles: (files) => set({ pendingFiles: files }),
  setStarting: (v) => set({ starting: v }),
  setHeroSync: (s) => set({ heroSync: s }),
}));
